import React, { useCallback, useEffect, useRef, useState } from "react";
import { AddIcon, EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { AdminService } from "src/rest/adminService";
import { UseAppToast } from "src/common/useAppToast";
import { StatusCode } from "src/common/constants/constantEnums";
import type {
  ConsultantUser,
  ConsultantFormState,
  ConsultantFormErrors,
} from "src/types/marriageConsultants.types";
import {
  CONSULTANT_EMPTY_FORM,
  CONSULTANT_TABLE_HEADERS,
  mapConsultantResponse,
  validateConsultantForm,
} from "src/utils/marriageConsultants.utils";

// ── Component ─────────────────────────────────────────────────────────

const MarriageConsultants: React.FC = () => {
  const { successToast, errorToast } = UseAppToast();

  // Stable ref so toast functions never appear in dependency arrays.
  const toastRef = useRef({ successToast, errorToast });
  toastRef.current = { successToast, errorToast };

  const [consultants, setConsultants] = useState<ConsultantUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [togglingEmail, setTogglingEmail] = useState<string | null>(null);

  // Drawer state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editTarget, setEditTarget] = useState<ConsultantUser | null>(null);
  const [form, setForm] = useState<ConsultantFormState>(CONSULTANT_EMPTY_FORM);
  const [errors, setErrors] = useState<ConsultantFormErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  const firstFieldRef = useRef<HTMLInputElement>(null);

  // ── Fetch ────────────────────────────────────────────────────────────

  const fetchConsultants = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await AdminService.getConsultants();
      if (res.status === StatusCode.SUCCESS_STATUS_CODE) {
        const raw = Array.isArray(res.data) ? res.data : [];
        setConsultants((raw as any[]).map(mapConsultantResponse));
      }
    } catch (err: any) {
      toastRef.current.errorToast(
        err?.response?.data?.message || "Failed to fetch consultants"
      );
    } finally {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void fetchConsultants();
  }, [fetchConsultants]);

  // ── Drawer helpers ───────────────────────────────────────────────────

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...CONSULTANT_EMPTY_FORM });
    setErrors({});
    onOpen();
  };

  const openEdit = (c: ConsultantUser) => {
    setEditTarget(c);
    setForm({
      email: c.email,
      fullName: c.fullName,
      mobileNo: c.mobileNo,
      dailyRequestCountLimit: c.dailyRequestCountLimit,
      marriage_profile_limit: c.marriage_profile_limit,
    });
    setErrors({});
    onOpen();
  };

  const handleClose = () => {
    setForm({ ...CONSULTANT_EMPTY_FORM });
    setErrors({});
    setEditTarget(null);
    onClose();
  };

  // ── Field helper ─────────────────────────────────────────────────────

  const setField = <K extends keyof ConsultantFormState>(
    key: K,
    value: ConsultantFormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // ── Save ─────────────────────────────────────────────────────────────

  const handleSave = async () => {
    const isEdit = editTarget !== null;
    const errs = validateConsultantForm(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit) {
        const payload: any = {
          email: form.email.trim(),
          fullName: form.fullName.trim() || undefined,
          mobileNo: form.mobileNo.trim(),
          approved: editTarget!.approved,
          dailyRequestCountLimit: Number(form.dailyRequestCountLimit),
          marriage_profile_limit: Number(form.marriage_profile_limit),
        };
        const res = await AdminService.updateConsultant(payload);
        if (
          res.status === StatusCode.SUCCESS_STATUS_CODE ||
          res.status === StatusCode.CREATED_STATUS_CODE
        ) {
          toastRef.current.successToast("Consultant updated successfully");
          handleClose();
          void fetchConsultants();
        }
      } else {
        const res = await AdminService.createConsultant({
          email: form.email.trim(),
          fullName: form.fullName.trim() || undefined,
          mobileNo: form.mobileNo.trim(),
          approved: true,
          dailyRequestCountLimit: Number(form.dailyRequestCountLimit),
          marriage_profile_limit: Number(form.marriage_profile_limit),
        });
        if (
          res.status === StatusCode.SUCCESS_STATUS_CODE ||
          res.status === StatusCode.CREATED_STATUS_CODE
        ) {
          toastRef.current.successToast("Consultant created successfully");
          handleClose();
          void fetchConsultants();
        }
      }
    } catch (err: any) {
      toastRef.current.errorToast(
        err?.response?.data?.message || "Failed to save consultant"
      );
    } finally {
      setIsSaving(false);
    }
  };

  // ── Toggle active ────────────────────────────────────────────────────

  const handleToggle = async (consultant: ConsultantUser) => {
    const next = !consultant.approved;
    setTogglingEmail(consultant.email);
    // Optimistic update
    setConsultants((prev) =>
      prev.map((c) =>
        c.email === consultant.email ? { ...c, approved: next } : c
      )
    );
    try {
      const res = await AdminService.updateConsultant({
        email: consultant.email,
        fullName: consultant.fullName || undefined,
        mobileNo: consultant.mobileNo,
        approved: next,
      });
      if (
        res.status !== StatusCode.SUCCESS_STATUS_CODE &&
        res.status !== StatusCode.CREATED_STATUS_CODE
      ) {
        throw new Error(`HTTP ${res.status}`);
      }
      toastRef.current.successToast(
        `Consultant marked ${next ? "Active" : "Inactive"}`
      );
    } catch (err: any) {
      // Rollback on failure
      setConsultants((prev) =>
        prev.map((c) =>
          c.email === consultant.email ? { ...c, approved: !next } : c
        )
      );
      toastRef.current.errorToast(
        err?.response?.data?.message || "Failed to update status"
      );
    } finally {
      setTogglingEmail(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <Box width="100%">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={8}>
        <Heading size="lg" color="#4A2C2A" fontWeight={700}>
          Marriage Consultants
        </Heading>
        <Button
          leftIcon={<AddIcon />}
          bg="#4A2C2A"
          color="white"
          onClick={openCreate}
          _hover={{ bg: "#6B0000" }}
          _active={{ bg: "#5B0000" }}
          fontWeight={600}
        >
          Add Consultant
        </Button>
      </Flex>

      {/* Table / empty / loading */}
      {isLoading ? (
        <Center py={20}>
          <Spinner size="lg" color="#4A2C2A" />
        </Center>
      ) : consultants.length === 0 ? (
        <Center py={20} borderRadius="md" border="1px solid #E5E7EB" bg="#F8F7F5">
          <Text fontSize="md" color="#5C5757">
            No consultants found. Click "Add Consultant" to create one.
          </Text>
        </Center>
      ) : (
        <TableContainer borderRadius="md" border="1px solid #E5E7EB" bg="white">
          <Table variant="simple" size="md">
            <Thead bg="#F8F7F5" borderBottom="2px solid #E5E7EB">
              <Tr>
                {CONSULTANT_TABLE_HEADERS.map((h) => (
                  <Th key={h} fontSize="sm" fontWeight={600} color="#2C292A">
                    {h}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {consultants.map((c) => (
                <Tr
                  key={c.email}
                  borderBottom="1px solid #E5E7EB"
                  _hover={{ bg: "#F8F7F5" }}
                >
                  <Td fontSize="sm" color="#5C5757">{c.id}</Td>
                  <Td fontSize="sm" color="#2C292A" fontWeight={500}>
                    {c.fullName || "—"}
                  </Td>
                  <Td fontSize="sm" color="#2C292A">{c.email}</Td>
                  <Td fontSize="sm" color="#5C5757">{c.mobileNo || "—"}</Td>
                  <Td fontSize="sm" color="#5C5757" textAlign="center">
                    {c.dailyRequestCountLimit}
                  </Td>
                  <Td fontSize="sm" color="#5C5757" textAlign="center">
                    {c.marriage_profile_limit}
                  </Td>
                  <Td>
                    <Badge
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight={600}
                      bg={c.approved ? "#E6F3E6" : "#FFE6E6"}
                      color={c.approved ? "#2D5F2D" : "#8B0000"}
                    >
                      {c.approved ? "ACTIVE" : "INACTIVE"}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Switch
                        isChecked={c.approved}
                        isDisabled={togglingEmail === c.email}
                        onChange={() => handleToggle(c)}
                        colorScheme="green"
                        size="md"
                        aria-label="Toggle active"
                      />
                      {togglingEmail === c.email && (
                        <Spinner size="xs" color="#4A2C2A" />
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <Button
                      leftIcon={<EditIcon />}
                      size="sm"
                      variant="outline"
                      borderColor="#4A2C2A"
                      color="#4A2C2A"
                      _hover={{ bg: "#FBF7F4" }}
                      onClick={() => openEdit(c)}
                    >
                      Edit
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Drawer */}
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleClose}
        size="md"
        initialFocusRef={firstFieldRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="lg" fontWeight={600} color="#4A2C2A">
            {editTarget ? "Edit Consultant" : "Add New Consultant"}
          </DrawerHeader>

          <DrawerBody>
            <VStack spacing={5} align="stretch" py={2}>

              {/* Full Name */}
              <FormControl>
                <FormLabel fontSize="sm" fontWeight={500}>Full Name</FormLabel>
                <Input
                  ref={editTarget !== null ? firstFieldRef : undefined}
                  type="text"
                  placeholder="Consultant's full name"
                  value={form.fullName}
                  onChange={(e) => setField("fullName", e.target.value)}
                  borderColor="#D0CFCF"
                />
              </FormControl>

              {/* Email — read-only in edit mode */}
              <FormControl isRequired isInvalid={!!errors.email}>
                <FormLabel fontSize="sm" fontWeight={500}>Email</FormLabel>
                <Input
                  ref={editTarget === null ? firstFieldRef : undefined}
                  type="email"
                  placeholder="consultant@example.com"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  borderColor={errors.email ? "#F41E1E" : "#D0CFCF"}
                  isReadOnly={editTarget !== null}
                  bg={editTarget !== null ? "#F3F4F6" : undefined}
                />
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              {/* Mobile */}
              <FormControl isRequired isInvalid={!!errors.mobileNo}>
                <FormLabel fontSize="sm" fontWeight={500}>Mobile Number</FormLabel>
                <Input
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={form.mobileNo}
                  maxLength={10}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setField("mobileNo", val);
                  }}
                  borderColor={errors.mobileNo ? "#F41E1E" : "#D0CFCF"}
                />
                <FormErrorMessage>{errors.mobileNo}</FormErrorMessage>
              </FormControl>

              {/* Daily Request Count Limit */}
              <FormControl isRequired isInvalid={!!errors.dailyRequestCountLimit}>
                <FormLabel fontSize="sm" fontWeight={500}>
                  Daily Request Count Limit
                </FormLabel>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.dailyRequestCountLimit}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                  }}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, "");
                    if (val !== "") val = String(Math.max(1, parseInt(val, 10)));
                    setField("dailyRequestCountLimit", val);
                  }}
                  borderColor={errors.dailyRequestCountLimit ? "#F41E1E" : "#D0CFCF"}
                />
                <FormErrorMessage>
                  {errors.dailyRequestCountLimit}
                </FormErrorMessage>
              </FormControl>

              {/* Marriage Profile Limit */}
              <FormControl isRequired isInvalid={!!errors.marriage_profile_limit}>
                <FormLabel fontSize="sm" fontWeight={500}>
                  Marriage Profile Limit
                </FormLabel>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.marriage_profile_limit}
                  onKeyDown={(e) => {
                    if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                  }}
                  onChange={(e) => {
                    let val = e.target.value.replace(/\D/g, "");
                    if (val !== "") val = String(Math.max(1, parseInt(val, 10)));
                    setField("marriage_profile_limit", val);
                  }}
                  borderColor={errors.marriage_profile_limit ? "#F41E1E" : "#D0CFCF"}
                />
                <FormErrorMessage>
                  {errors.marriage_profile_limit}
                </FormErrorMessage>
              </FormControl>
            </VStack>
          </DrawerBody>

          <DrawerFooter borderTop="1px solid #E5E7EB">
            <HStack spacing={3} w="full" justify="flex-end">
              <Button
                variant="outline"
                onClick={handleClose}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                bg="#4A2C2A"
                color="white"
                _hover={{ bg: "#6B0000" }}
                onClick={handleSave}
                isLoading={isSaving}
                loadingText={editTarget ? "Saving…" : "Creating…"}
              >
                {editTarget ? "Save Changes" : "Create Consultant"}
              </Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default MarriageConsultants;
